import { CarbonIntensity } from '@/models/carbon/CarbonIntensity.js'
import { ImpactIntensity } from '@/models/carbon/ImpactIntensity.js'
import VEHICLES_FACTORS from '@/../data/factors/carbon/vehiclesFactors.json'
import TRANSPORTS_FACTORS from '@/../data/factors/carbon/transportsFactors.json'
import VEHICLES_LCA_FACTORS from '@/../data/factors/lca/vehiclesFactors.json'
import TRANSPORTS_LCA_FACTORS from '@/../data/factors/lca/transportsFactors.json'
import { EmissionFactor, ImpactFactor } from '@/models/carbon/Factor.js'

export default class CommuteSection {
  constructor ({
    mode = null,
    distance = 0,
    isDay2 = false,
    pooling = 1,
    engine = null
  } = {}) {
    this.mode = mode
    this.distance = distance
    this.isDay2 = isDay2
    this.pooling = pooling
    this.engine = engine
  }

  get isPooling () {
    return this.pooling > 1
  }

  __getFactorKeys (citySize = null) {
    let keys = [null, null]
    if (this.mode === 'bike') {
      keys = ['bike', 'muscular']
    } else if (this.mode === 'ebike') {
      keys = ['bike', 'electric']
    } else if (this.mode === 'escooter') {
      keys = ['scooter', 'electric']
    } else if (this.mode === 'motorbike') {
      keys = ['motorbike', 'gasoline']
    } else if (this.mode === 'car') {
      keys = ['car', this.engine]
    } else if (this.mode === 'bus') {
      if (citySize === 0) {
        keys = ['bus', 'bus.smallcity']
      } else if (citySize === 1) {
        keys = ['bus', 'bus.mediumcity']
      } else {
        keys = ['bus', 'bus.bigcity']
      }
    } else if (this.mode === 'busintercity') {
      keys = ['bus', 'bus.intercity']
    } else if (this.mode === 'train') {
      // The survey asks for dailyDistance, to choose the emission
      // factor, we considere its go and back
      let commuteDistance = this.distance / 2
      if (commuteDistance < 200) {
        keys = ['railway', 'train.shortdistance']
      } else {
        keys = ['railway', 'train.longdistance']
      }
    } else if (this.mode === 'tram') {
      if (citySize === 0) {
        keys = ['railway', 'tram.mediumcity']
      } else if (citySize === 1) {
        keys = ['railway', 'tram.mediumcity']
      } else {
        keys = ['railway', 'tram.bigcity']
      }
    } else if (this.mode === 'expressrailway') {
      keys = ['railway', 'rer']
    } else if (this.mode === 'subway') {
      keys = ['railway', 'subway']
    } else if (this.mode === 'cablecar') {
      keys = ['guided.transport', 'cable.car']
    }
    return keys
  }

  getFactor (citySize = null, isCarbon = true) {
    // first get the right factor
    let keys = this.__getFactorKeys(citySize)
    let factor = EmissionFactor.createFromObj()
    if (!isCarbon) {
      factor = ImpactFactor.createFromObj()
    }
    if (isCarbon && this.mode === 'walking') {
      factor.group = 'walking'
    } else if (
      ['bike', 'ebike', 'escooter', 'motorbike', 'car'].includes(this.mode)
    ) {
      if (isCarbon) {
        factor = EmissionFactor.createFromObj(
          VEHICLES_FACTORS[keys[0]][keys[1]]
        )
      } else {
        factor = ImpactFactor.createFromObj(
          VEHICLES_LCA_FACTORS[keys[0]][keys[1]]
        )
      }
    } else if (
      [
        'bus',
        'busintercity',
        'train',
        'tram',
        'expressrailway',
        'subway'
      ].includes(this.mode)
    ) {
      if (isCarbon) {
        factor = EmissionFactor.createFromObj(
          TRANSPORTS_FACTORS[keys[0]][keys[1]]
        )
      } else {
        factor = ImpactFactor.createFromObj(
          TRANSPORTS_LCA_FACTORS[keys[0]][keys[1]]
        )
      }
    }
    return factor
  }

  getCarbonIntensity (citySize, year = null) {
    let ef = this.getFactor(citySize).getFactor(year)
    let distance = this.distance
    if (this.mode === 'car' || this.mode === 'motorbike') {
      distance = distance / this.pooling
    }
    return new CarbonIntensity(
      distance * ef.total.total,
      distance * ef.total.total * ef.total.uncertainty,
      ef.group
    )
  }

  getImpact (citySize, year = null) {
    let ifactor = this.getFactor(citySize, false)
    let distance = this.distance
    if (this.mode === 'car' || this.mode === 'motorbike') {
      distance = distance / this.pooling
    }
    return ImpactIntensity.compute(distance, ifactor)
  }

  static createFromObj (section) {
    return new CommuteSection({
      mode: section.mode,
      distance: section.distance,
      isDay2: section.isDay2,
      pooling: section.pooling,
      engine: section.engine
    })
  }
}
